import { motion } from 'framer-motion';
import React, { LegacyRef, useEffect, useState } from 'react';
import ReactCountryFlag from 'react-country-flag';
import { FaChevronDown } from 'react-icons/fa6';
import { DEFAULT_REST_COUNTRY_SELECT_VALUE } from '../../../constants/settings';
import useCountryActions from '../../../hooks/APIHooks/users/useCountryActions';
import useRestCountries from '../../../hooks/useRestCountries';
import useUserClickObserver from '../../../hooks/useUserClickObserver';
import { RestCountry } from '../../../types/common';
import { CountryReqBody } from '../../../types/countries';

interface IAcademiaRestCountrySelect {
  name: string;
  label: string;
  setCountry: React.Dispatch<React.SetStateAction<CountryReqBody | null>>;
  resetCountry: boolean;
  setResetCountry: React.Dispatch<React.SetStateAction<boolean>>;
  selectedCountries: Array<CountryReqBody>;
  isRequired?: boolean;
  isDisabled?: boolean;
}

const AcademiaRestCountrySelect = ({
  name,
  label,
  setCountry,
  resetCountry,
  setResetCountry,
  selectedCountries,
  isRequired,
  isDisabled,
}: IAcademiaRestCountrySelect) => {
  // integration of react hooks here
  const [searchText, setSearchText] = useState<string>('');
  const [showCountries, setShowCountries] = useState<boolean>(false);
  const [allCountries, setAllCountries] = useState<Array<RestCountry>>([]);
  const [selectedCountry, setSelectedCountry] = useState<RestCountry>(
    DEFAULT_REST_COUNTRY_SELECT_VALUE
  );

  // integration of custom API hooks here
  const countries = useRestCountries();
  const { getCountryListHandler } = useCountryActions('countryList');

  // integration of custom hooks here
  const selectRef = useUserClickObserver(setShowCountries);

  // setting default value of country here
  useEffect(() => {
    if (resetCountry) {
      setSelectedCountry(DEFAULT_REST_COUNTRY_SELECT_VALUE);
      setCountry(null);

      setResetCountry(false);
    }
  }, [setCountry, resetCountry, setResetCountry]);

  // setting countries based on search result declared here
  useEffect(() => {
    if (countries) {
      if (searchText) {
        setAllCountries(
          countries.filter((country) =>
            country.name.common.toLowerCase().includes(searchText.toLowerCase())
          )
        );
      } else {
        setAllCountries(countries);
      }
    }
  }, [searchText, countries, setCountry]);

  // handler function to select country from the list declared here
  const selectCountryHandler = (country: RestCountry) => {
    const selectedCountry: CountryReqBody = {
      country_name: country.name.common,
      iso_code: country.cca2,
      isd_code: `${country.idd.root}${country.idd.suffixes[0]}`,
    };

    setSelectedCountry(country);
    setCountry(selectedCountry);

    setSearchText('');
    setShowCountries(false);
  };

  // rendering academia rest country select component here
  return (
    <div>
      <label
        htmlFor={`input-${name}`}
        title={isRequired ? 'This field is required' : ''}
        className='block mb-1 text-base-dark-blue font-medium text-base'
      >
        {label} {isRequired && <span className='text-base-red'>*</span>}
      </label>

      <div
        tabIndex={isDisabled ? -1 : 0}
        ref={selectRef as LegacyRef<HTMLDivElement>}
        className='relative w-full focus:ring-0 focus:outline-none'
        onFocus={() => !isDisabled && setShowCountries(true)}
        onBlur={() => setShowCountries(false)}
      >
        <div
          title={selectedCountry.name.common}
          // onClick={() => setShowCountries((state) => !state)}
          className={`relative peer/select border-2 rounded-md p-3 text-base-dark-blue font-semibold duration-300 text-base border-base-dark-blue w-full md:min-w-[160px] ${
            isDisabled
              ? 'opacity-70 cursor-not-allowed'
              : 'active:scale-95 cursor-pointer'
          }`}
        >
          <div className='flex items-center gap-2'>
            {selectedCountry.cca2 && (
              <ReactCountryFlag
                style={{ width: '2em', height: '1.42em' }}
                countryCode={selectedCountry.cca2}
                svg
              />
            )}
            <p className='font-semibold'>{`${selectedCountry.name.common}`}</p>
          </div>

          <div
            className={`absolute right-3 top-1/2 -translate-y-1/2 duration-300 bg-base-white ${
              showCountries ? 'rotate-180' : 'rotate-0'
            }`}
          >
            <FaChevronDown className='h-6 w-6' />
          </div>
        </div>

        <motion.div
          initial={{ height: 0 }}
          animate={showCountries ? { height: 'fit-content' } : { height: 0 }}
          className='absolute left-0 top-full bg-base-white shadow-academia-sm w-full z-20 mt-2 rounded-md text-base-dark-blue overflow-hidden text-base'
        >
          <div className='m-3'>
            <input
              tabIndex={-1}
              type='text'
              className='border-2 focus:ring-4 focus:ring-base-dark-blue rounded-md px-3 py-2 w-full text-base-dark-blue font-semibold duration-300 text-base border-base-dark-blue active:scale-[0.98]'
              placeholder='Search countries...'
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </div>

          <motion.ul
            initial={{ height: 0 }}
            animate={
              showCountries
                ? allCountries.length > 5
                  ? { height: 220 }
                  : { height: 'fit-content' }
                : { height: 0 }
            }
            className='academia-scrollbar overflow-y-auto'
          >
            {allCountries.length && getCountryListHandler.data?.is_success
              ? allCountries
                  .filter(
                    (country) =>
                      !getCountryListHandler.data.data.some(
                        (addedCountry) => addedCountry.iso_code === country.cca2
                      )
                  )
                  .filter(
                    (country) =>
                      !selectedCountries.some(
                        (selectedCountry) =>
                          selectedCountry.iso_code === country.cca2
                      )
                  )
                  .map(
                    (country, index) =>
                      country.idd.root && (
                        <motion.li
                          initial={{ opacity: 0 }}
                          title={country.name.common}
                          animate={
                            showCountries ? { opacity: 1 } : { opacity: 0 }
                          }
                          key={index}
                          onClick={() => {
                            selectCountryHandler(country);
                            selectRef.current?.blur();
                          }}
                          className='py-2 px-3 hover:bg-gray-200 duration-300 cursor-pointer [&:not(:last-child)]:border-b border-gray-300'
                        >
                          <div className='flex items-center gap-3'>
                            <ReactCountryFlag
                              style={{ width: '2em', height: '2em' }}
                              countryCode={country.cca2}
                              svg
                            />
                            <p className='font-semibold'>{`${country.name.common}`}</p>
                          </div>
                        </motion.li>
                      )
                  )
              : null}
          </motion.ul>
        </motion.div>
      </div>
    </div>
  );
};

export default AcademiaRestCountrySelect;
